import React, { useEffect, useState } from 'react'
import { Stack, Box } from '@chakra-ui/react'
import { COLUMN_WIDTH, MAX_COLUMN_WIDTH } from '../../utils'
import { LabeledInput, Button, Alert } from '@apex/component-library'
import { useAccountLookup, useIsThirdParty } from '../../api'
import isEmpty from 'lodash.isempty'
import { AccountLookupDetails } from '../AccountLookupDetails'
import {
  ACCOUNT_RESTRICTION_WARNING,
  ERROR_RETRIEVING_DATA,
  THIRD_PARTY_TRANSFER,
  ACCOUNT_NOT_FOUND
} from '../../constants'
import { AccountData } from '@Types'
import { useQueryClient } from 'react-query'
import queryKeys from '../../api/queryKeys'

interface ToAccountLookupProps {
  onChange: (e: React.ChangeEvent<any>) => void
  onBlur: (e: React.FocusEvent<any>) => void
  errorMessage?: string
  toAccount: string
  fromAccount: string
  touched?: boolean
  checkThirdParty: boolean
  onLookupSubmit: (data?: AccountData) => void
}

const ToAccountLookup = ({
  fromAccount,
  toAccount,
  onChange,
  onBlur,
  errorMessage,
  touched,
  checkThirdParty = true,
  onLookupSubmit
}: ToAccountLookupProps) => {
  const [accountLookupNum, setAccountLookupNum] = useState<string>('')
  const [accountLookupError, setAccountLookupError] = useState<string>('')
  const [warningMessage, setWarningMessage] = useState<string>('')

  const { data, error, isLoading, refetch } = useAccountLookup(toAccount)
  const { isThirdPartyData, isThirdPartyError } = useIsThirdParty(fromAccount, accountLookupNum, checkThirdParty)
  const queryClient = useQueryClient()

  const handleLookupSubmit = () => {
    setAccountLookupNum(toAccount)
    refetch()
    setAccountLookupError('')
    setWarningMessage('')
  }

  useEffect(() => {
    if (data?.restrictionCode) {
      setWarningMessage(ACCOUNT_RESTRICTION_WARNING)
    } else if (Number(error?.response?.status) >= 500 || isThirdPartyError) {
      setWarningMessage(ERROR_RETRIEVING_DATA)
    } else if (isThirdPartyData?.isThirdParty) {
      //this is in the same if statement because restrictions take precedence over third party messages
      setWarningMessage(THIRD_PARTY_TRANSFER)
    } else {
      setWarningMessage('')
    }
    if (!!error) {
      setAccountLookupError(ACCOUNT_NOT_FOUND)
    } else {
      setAccountLookupError('')
    }
  }, [data, isThirdPartyData, isThirdPartyError, error])

  useEffect(() => {
    onLookupSubmit(data)
  }, [data, onLookupSubmit])

  return (
    <Stack spacing={6}>
      <Stack direction='row' spacing={4}>
        <Box width={COLUMN_WIDTH} maxW={MAX_COLUMN_WIDTH}>
          <LabeledInput
            name='toAccount'
            label='To Account'
            className={'test-id-to-account'}
            onChange={(e) => {
              onChange(e)
              queryClient.invalidateQueries(queryKeys.accountLookup)
            }}
            isRequired
            errorMessage={accountLookupError || errorMessage}
            isInvalid={touched && (!!error || !!errorMessage)}
            onBlur={onBlur}
            value={toAccount}
            data-testid='toAccount'
          />
        </Box>
        <Box paddingTop={'26px'}>
          <Button
            onClick={handleLookupSubmit}
            variant='secondary'
            isLoading={isLoading}
            type={'button'}
            className={'test-id-lookup-btn'}
          >
            Look-up
          </Button>
        </Box>
      </Stack>
      {data && !error && !isEmpty(data.accountTitle) && (
        <AccountLookupDetails account={{ ...data, accountNumber: accountLookupNum }} />
      )}

      {!isEmpty(warningMessage) && <Alert status='warning' description={warningMessage} showIcon={true} />}
    </Stack>
  )
}

export default ToAccountLookup
